import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import "@/App.css";

// Customer pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import FortuneFlow from "@/pages/FortuneFlow";
import FortuneResult from "@/pages/FortuneResult";
import History from "@/pages/History";
import Store from "@/pages/Store";
import PaymentReturn from "@/pages/PaymentReturn";
import Profile from "@/pages/Profile";

// Admin pages
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminFortuneTypes from "@/pages/admin/AdminFortuneTypes";
import AdminAIProviders from "@/pages/admin/AdminAIProviders";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminWhitelist from "@/pages/admin/AdminWhitelist";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminFortuneRequests from "@/pages/admin/AdminFortuneRequests";
import AdminPushBroadcast from "@/pages/admin/AdminPushBroadcast";
import AdminI18n from "@/pages/admin/AdminI18n";
import AdminFalcilar from "@/pages/admin/AdminFalcilar";
import AdminFalciApplications from "@/pages/admin/AdminFalciApplications";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminConsultations from "@/pages/admin/AdminConsultations";
import AdminPasswordResets from "@/pages/admin/AdminPasswordResets";
import LegalPage from "@/pages/LegalPage";
import Falcilar from "@/pages/Falcilar";
import FalciBasvuru from "@/pages/FalciBasvuru";
import FalciPanel from "@/pages/FalciPanel";
import ChatRoom from "@/pages/ChatRoom";
import ForgotPassword from "@/pages/ForgotPassword";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="cosmic-bg min-h-screen flex items-center justify-center"><div className="stars"/><p className="gold-text font-serif-myst text-2xl">Yükleniyor...</p></div>;
  if (!user) return <Navigate to="/giris" replace />;
  return children;
}

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
        <Toaster position="top-center" richColors theme="dark" />
        <Routes>
          {/* Customer routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/giris" element={<Login />} />
          <Route path="/kayit" element={<Register />} />
          <Route path="/panel" element={<Protected><Dashboard /></Protected>} />
          <Route path="/fal/:slug" element={<Protected><FortuneFlow /></Protected>} />
          <Route path="/sonuc/:id" element={<Protected><FortuneResult /></Protected>} />
          <Route path="/gecmis" element={<Protected><History /></Protected>} />
          <Route path="/magaza" element={<Protected><Store /></Protected>} />
          <Route path="/falcilar" element={<Protected><Falcilar /></Protected>} />
          <Route path="/fal-bakmak-ister-misin" element={<Protected><FalciBasvuru /></Protected>} />
          <Route path="/falci-panel" element={<Protected><FalciPanel /></Protected>} />
          <Route path="/sohbet/:id" element={<Protected><ChatRoom /></Protected>} />
          <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
          <Route path="/payment/success" element={<Protected><PaymentReturn /></Protected>} />
          <Route path="/payment/cancel" element={<Protected><PaymentReturn cancel /></Protected>} />
          <Route path="/profil" element={<Protected><Profile /></Protected>} />
          <Route path="/kvkk" element={<LegalPage />} />
          <Route path="/kullanim" element={<LegalPage />} />

          {/* Admin routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/*" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="fortune-types" element={<AdminFortuneTypes />} />
            <Route path="fortune-requests" element={<AdminFortuneRequests />} />
            <Route path="ai" element={<AdminAIProviders />} />
            <Route path="packages" element={<AdminPackages />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="push" element={<AdminPushBroadcast />} />
            <Route path="whitelist" element={<AdminWhitelist />} />
            <Route path="transactions" element={<AdminTransactions />} />
            <Route path="banners" element={<AdminBanners />} />
            <Route path="i18n" element={<AdminI18n />} />
            <Route path="falcilar" element={<AdminFalcilar />} />
            <Route path="falci-applications" element={<AdminFalciApplications />} />
            <Route path="consultations" element={<AdminConsultations />} />
            <Route path="withdrawals" element={<AdminWithdrawals />} />
            <Route path="password-resets" element={<AdminPasswordResets />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
